import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useEditorContext, TEXT_ELEMENT_IDS, type TextElementId } from '../context/EditorContext';
import type { TemplateLayer, LayerPosition } from '../types';


// ─── Snap helper ─────────────────────────────────────────────

const GRID_SIZE = 10; // px in composition-space

function snap(v: number, enabled: boolean): number {
  if (!enabled) return v;
  return Math.round(v / GRID_SIZE) * GRID_SIZE;
}

// ─── Layer type colours for the overlay labels ────────────────

const LAYER_COLORS: Record<string, string> = {
  background: '#4ade80',
  anchor: '#60a5fa',
  logo: '#f59e0b',
  'lower-third': '#a78bfa',
  ticker: '#f97316',
  'breaking-news-banner': '#ef4444',
  'top-bar': '#06b6d4',
  watermark: '#94a3b8',
  audio: '#ec4899',
  image: '#22d3ee',
  video: '#818cf8',
};

// ─── Resize handle positions ─────────────────────────────────

type HandleDir = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'w' | 'e';

const HANDLE_CURSORS: Record<HandleDir, string> = {
  nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
  n: 'n-resize', s: 's-resize', w: 'w-resize', e: 'e-resize',
};

// ─── Individual layer overlay ─────────────────────────────────

interface LayerOverlayProps {
  layer: TemplateLayer;
  compW: number;
  compH: number;
  scale: number;
  snapGrid: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onPositionChange: (pos: Partial<LayerPosition>) => void;
}

function LayerOverlay({
  layer,
  compW,
  compH,
  scale,
  snapGrid,
  isSelected,
  onSelect,
  onPositionChange,
}: LayerOverlayProps) {
  const { position, type, label, locked } = layer;
  const color = LAYER_COLORS[type] ?? '#94a3b8';

  const displayX = position.x * scale;
  const displayY = position.y * scale;
  const displayW = position.width * scale;
  const displayH = position.height * scale;
  const rotation = position.rotation ?? 0;

  // Drag state stored in refs to avoid re-renders during drag
  const dragRef = useRef<{
    startMouseX: number;
    startMouseY: number;
    startLayerX: number;
    startLayerY: number;
    mode: 'move' | HandleDir;
    startLayerW: number;
    startLayerH: number;
  } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, mode: 'move' | HandleDir = 'move') => {
      if (locked) return;
      e.stopPropagation();
      e.preventDefault();
      onSelect();

      dragRef.current = {
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startLayerX: position.x,
        startLayerY: position.y,
        startLayerW: position.width,
        startLayerH: position.height,
        mode,
      };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = (ev.clientX - dragRef.current.startMouseX) / scale;
        const dy = (ev.clientY - dragRef.current.startMouseY) / scale;
        const { mode: m, startLayerX, startLayerY, startLayerW, startLayerH } = dragRef.current;

        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        const minSize = 10;

        if (m === 'move') {
          onPositionChange({
            x: snap(clamp(startLayerX + dx, 0, compW - position.width), snapGrid),
            y: snap(clamp(startLayerY + dy, 0, compH - position.height), snapGrid),
          });
        } else {
          let x = startLayerX, y = startLayerY, w = startLayerW, h = startLayerH;

          if (m.includes('e')) { w = snap(Math.max(minSize, startLayerW + dx), snapGrid); }
          if (m.includes('s')) { h = snap(Math.max(minSize, startLayerH + dy), snapGrid); }
          if (m.includes('w')) { const nw = snap(Math.max(minSize, startLayerW - dx), snapGrid); x = startLayerX + (startLayerW - nw); w = nw; }
          if (m.includes('n')) { const nh = snap(Math.max(minSize, startLayerH - dy), snapGrid); y = startLayerY + (startLayerH - nh); h = nh; }

          onPositionChange({ x, y, width: w, height: h });
        }
      };

      const onMouseUp = () => {
        dragRef.current = null;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [locked, onSelect, position, scale, compW, compH, onPositionChange, snapGrid]
  );

  const isAudio = type === 'audio';

  return (
    <div
      onMouseDown={(e) => handleMouseDown(e, 'move')}
      style={{
        position: 'absolute',
        left: displayX,
        top: displayY,
        width: displayW,
        height: isAudio ? Math.max(displayH, 28 * scale) : displayH,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformOrigin: 'top left',
        cursor: locked ? 'not-allowed' : isSelected ? 'move' : 'pointer',
        boxSizing: 'border-box',
        // Transparent background so the Remotion player renders through
        background: isSelected ? `${color}11` : 'transparent',
        outline: isSelected
          ? `2px solid ${color}`
          : `1px dashed ${color}44`,
        zIndex: 50 + position.zIndex,
        userSelect: 'none',
      }}
    >
      {/* Layer label — only when selected */}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: -22,
            left: 0,
            background: color,
            color: '#000',
            fontSize: 10,
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 3,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {locked ? '🔒 ' : ''}{label ?? layer.id}
        </div>
      )}

      {/* Audio layers: show a subtle pill so the user knows where it sits */}
      {isAudio && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', paddingLeft: 6,
          fontSize: 10, color, background: `${color}18`,
          fontWeight: 600, gap: 4, pointerEvents: 'none',
        }}>
          <span>♪</span><span>{label}</span>
        </div>
      )}

      {/* Resize handles — only when selected and not locked */}
      {isSelected && !locked && !isAudio && (
        <>
          {(['nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e'] as HandleDir[]).map((dir) => {
            const isCorner = dir.length === 2;
            const hSize = 8;

            const style: React.CSSProperties = {
              position: 'absolute',
              width: isCorner ? hSize : dir === 'n' || dir === 's' ? displayW * 0.4 : hSize,
              height: isCorner ? hSize : dir === 'w' || dir === 'e' ? displayH * 0.4 : hSize,
              background: color,
              borderRadius: 2,
              cursor: HANDLE_CURSORS[dir],
              zIndex: 10,
              transform: 'translate(-50%, -50%)',
              top: dir.includes('n') ? 0 : dir.includes('s') ? '100%' : '50%',
              left: dir.includes('w') ? 0 : dir.includes('e') ? '100%' : '50%',
            };

            return (
              <div
                key={dir}
                style={style}
                onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, dir); }}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

// ─── Text element overlay (Headline / Subheadline) ───────────

interface TextElementOverlayProps {
  id: TextElementId;
  label: string;
  position: LayerPosition;
  font: Record<string, unknown>;
  textContent: string;
  compW: number;
  compH: number;
  scale: number;
  snapGrid: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onPositionChange: (pos: Partial<LayerPosition>) => void;
}

function TextElementOverlay({
  id, label, position, font, textContent, compW, compH, scale, snapGrid, isSelected, onSelect, onPositionChange,
}: TextElementOverlayProps) {
  const color = id === TEXT_ELEMENT_IDS.HEADLINE ? '#fbbf24' : '#a3e635';
  const dragRef = useRef<{
    startMouseX: number; startMouseY: number;
    startX: number; startY: number;
    startW: number; startH: number;
    mode: 'move' | 'nw' | 'ne' | 'sw' | 'se';
  } | null>(null);

  const displayX = position.x * scale;
  const displayY = position.y * scale;
  const displayW = position.width * scale;
  const displayH = position.height * scale;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, mode: 'move' | 'nw' | 'ne' | 'sw' | 'se' = 'move') => {
      e.stopPropagation();
      e.preventDefault();
      onSelect();
      dragRef.current = {
        startMouseX: e.clientX, startMouseY: e.clientY,
        startX: position.x, startY: position.y,
        startW: position.width, startH: position.height,
        mode,
      };
      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = (ev.clientX - dragRef.current.startMouseX) / scale;
        const dy = (ev.clientY - dragRef.current.startMouseY) / scale;
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        const { mode: m, startX, startY, startW, startH } = dragRef.current;
        if (m === 'move') {
          onPositionChange({
            x: snap(clamp(startX + dx, 0, compW - position.width), snapGrid),
            y: snap(clamp(startY + dy, 0, compH - position.height), snapGrid),
          });
        } else {
          let x = startX, y = startY, w = startW, h = startH;
          if (m.includes('e')) w = snap(Math.max(20, startW + dx), snapGrid);
          if (m.includes('s')) h = snap(Math.max(10, startH + dy), snapGrid);
          if (m.includes('w')) { const nw = snap(Math.max(20, startW - dx), snapGrid); x = startX + (startW - nw); w = nw; }
          if (m.includes('n')) { const nh = snap(Math.max(10, startH - dy), snapGrid); y = startY + (startH - nh); h = nh; }
          onPositionChange({ x, y, width: w, height: h });
        }
      };
      const onUp = () => { dragRef.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [position, scale, compW, compH, onSelect, onPositionChange, snapGrid]
  );

  return (
    <div
      onMouseDown={(e) => handleMouseDown(e, 'move')}
      style={{
        position: 'absolute', left: displayX, top: displayY, width: displayW, height: displayH,
        boxSizing: 'border-box',
        // Use actual zIndex so higher-z layers intercept clicks first;
        // base of 50 keeps all overlays above the Remotion player slot (z=1)
        zIndex: 50 + position.zIndex,
        outline: isSelected ? `2px solid ${color}` : `1px dashed ${color}66`,
        cursor: isSelected ? 'move' : 'pointer', userSelect: 'none',
      }}
    >
      {isSelected && (
        <div style={{
          position: 'absolute', top: -20, left: 0, background: color, color: '#000',
          fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>T {label}</div>
      )}
      {/* Corner handles */}
      {isSelected && (['nw', 'ne', 'sw', 'se'] as const).map((dir) => (
        <div key={dir} onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, dir); }}
          style={{
            position: 'absolute', width: 8, height: 8, background: color, borderRadius: 2,
            cursor: `${dir}-resize`, zIndex: 10, transform: 'translate(-50%, -50%)',
            top: dir.includes('n') ? 0 : '100%',
            left: dir.includes('w') ? 0 : '100%',
          }}
        />
      ))}
      {/* Live text preview — scaled font, real content */}
      {(() => {
        const ta = String(font.textAlign ?? 'left');
        const justifyContent: React.CSSProperties['justifyContent'] =
          ta === 'center' ? 'center' : ta === 'right' ? 'flex-end' : 'flex-start';
        const alignItems: React.CSSProperties['alignItems'] =
          ta === 'center' ? 'center' : 'flex-start';
        return (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            justifyContent,
            alignItems,
            padding: `0 ${Math.max(2, 6 * scale)}px`,
            overflow: 'hidden', pointerEvents: 'none',
          }}>
            <span style={{
              fontSize: Math.max(8, Number(font.fontSize ?? 48) * scale),
              fontWeight: Number(font.fontWeight ?? 700) as React.CSSProperties['fontWeight'],
              color: textContent ? String(font.color ?? '#ffffff') : `${color}88`,
              textTransform: String(font.textTransform ?? 'none') as React.CSSProperties['textTransform'],
              textAlign: ta as React.CSSProperties['textAlign'],
              letterSpacing: `${Number(font.letterSpacing ?? 0) * scale}px`,
              lineHeight: Number(font.lineHeight ?? 1.2),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}>
              {textContent || `[${label}]`}
            </span>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Editor Canvas ────────────────────────────────────────────

interface EditorCanvasProps {
  compWidth: number;
  compHeight: number;
  playerSlot?: React.ReactNode;
  bgColor?: string;
  /** Actual headline text — shown live in the headline overlay */
  headlineText?: string;
  /** Actual subheadline text — shown live in the subheadline overlay */
  subheadlineText?: string;
}

export function EditorCanvas({
  compWidth,
  compHeight,
  playerSlot,
  bgColor = '#0a0f1e',
  headlineText = '',
  subheadlineText = '',
}: EditorCanvasProps) {
  const { state, selectLayer, updateLayerPosition } = useEditorContext();
  const { editableConfig, selectedLayerId, mode, zoom, snapGrid } = state;

  const containerRef = useRef<HTMLDivElement>(null);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  // Measure container to compute scale
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDisplaySize({ w: width, h: height });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Compute fit-scale and apply zoom
  const fitScale =
    displaySize.w > 0 && displaySize.h > 0
      ? Math.min(displaySize.w / compWidth, displaySize.h / compHeight)
      : 1;
  const scale = fitScale * zoom;

  const canvasW = compWidth * scale;
  const canvasH = compHeight * scale;

  const layers = editableConfig?.layers ?? [];

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#111',
        overflow: 'hidden',
        cursor: 'default',
        // NOTE: no onClick here — clicking canvas background no longer deselects.
        // Use Escape key or select another layer to change selection.
      }}
    >
      {/* The scaled canvas */}
      <div
        style={{
          position: 'relative',
          width: canvasW,
          height: canvasH,
          background: bgColor,
          flexShrink: 0,
          boxShadow: '0 0 0 1px #ffffff22, 0 4px 32px #00000088',
          overflow: 'hidden',
        }}
      >
        {/* ── Remotion Player — visible in BOTH edit and preview modes ── */}
        {/* In edit mode: loops/plays behind transparent overlays so content is always visible */}
        {/* In preview mode: full interactive player */}
        {playerSlot && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              pointerEvents: mode === 'preview' ? 'all' : 'none',
            }}
          >
            {playerSlot}
          </div>
        )}

        {/* ── Edit mode: transparent interaction overlays (Remotion player renders content behind) ── */}
        {mode === 'edit' &&
          layers
            .filter((l) => l.visible)
            .sort((a, b) => a.position.zIndex - b.position.zIndex)
            .map((layer) => (
              <LayerOverlay
                key={layer.id}
                layer={layer}
                compW={compWidth}
                compH={compHeight}
                scale={scale}
                snapGrid={snapGrid}
                isSelected={layer.id === selectedLayerId}
                onSelect={() => selectLayer(layer.id)}
                onPositionChange={(pos) => updateLayerPosition(layer.id, pos)}
              />
            ))}

        {/* ── Edit mode: headline overlay ── */}
        {mode === 'edit' && editableConfig?.headline && (editableConfig.headline.visible ?? true) && (
          <TextElementOverlay
            id={TEXT_ELEMENT_IDS.HEADLINE}
            label="Headline"
            position={editableConfig.headline.position}
            font={editableConfig.headline.font as unknown as Record<string, unknown>}
            textContent={headlineText}
            compW={compWidth}
            compH={compHeight}
            scale={scale}
            snapGrid={snapGrid}
            isSelected={selectedLayerId === TEXT_ELEMENT_IDS.HEADLINE}
            onSelect={() => selectLayer(TEXT_ELEMENT_IDS.HEADLINE)}
            onPositionChange={(pos) => updateLayerPosition(TEXT_ELEMENT_IDS.HEADLINE, pos)}
          />
        )}

        {/* ── Edit mode: subheadline overlay ── */}
        {mode === 'edit' && editableConfig?.subheadline && (editableConfig.subheadline.visible ?? true) && (
          <TextElementOverlay
            id={TEXT_ELEMENT_IDS.SUBHEADLINE}
            label="Subheadline"
            position={editableConfig.subheadline.position}
            font={editableConfig.subheadline.font as unknown as Record<string, unknown>}
            textContent={subheadlineText}
            compW={compWidth}
            compH={compHeight}
            scale={scale}
            snapGrid={snapGrid}
            isSelected={selectedLayerId === TEXT_ELEMENT_IDS.SUBHEADLINE}
            onSelect={() => selectLayer(TEXT_ELEMENT_IDS.SUBHEADLINE)}
            onPositionChange={(pos) => updateLayerPosition(TEXT_ELEMENT_IDS.SUBHEADLINE, pos)}
          />
        )}

        {/* ── Empty state: no template loaded ── */}
        {layers.length === 0 && !playerSlot && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#ffffff33', fontSize: 14, fontWeight: 500,
          }}>
            Select a template to start editing
          </div>
        )}
      </div>
    </div>
  );
}
